<template>
    <div ref="adminTabItems" class="tab-items mb-3">
        <EMDataTable ref="membersTable" :column-def="columnDef" :data-url="'/admin/members'"  @data-loaded="helper.loaderOff(adminTabItems)" @loading-data="helper.loaderOn(adminTabItems)">
            <template v-slot:username="{ rowData, column }">
                <td :style="column.width ? 'width: '+column.width+'%' : ''">
                    <p class="text-dark mb-0">{{ rowData.username }}</p>
                    <p class="m-0 text-muted fs-sm text-capitalize">{{ rowData.type }}</p>
                </td>
            </template>
            <template v-slot:status="{ rowData, column }">
                <td class="text-center" :style="column.width ? 'width: '+column.width+'%' : ''">
                    <span class="text-capitalize badge" :class="rowData.status == 'active' ? 'badge-success' : 'badge-danger'">{{ rowData.status }}</span>
                </td>
            </template>
            <template v-slot:action="{ rowData, column }">
                <td :style="column.width ? 'width: '+column.width+'%' : ''">
                    <div class="d-flex gap-2 justify-content-end">
                        <i class="bi bi-ban text-warning hoverable-icon" v-tooltip="'Block User'" v-if="rowData.status == 'active'" @click="openStatusModal(rowData.id, rowData.status)"></i>
                        <i class="bi bi-check2-circle text-info hoverable-icon" v-tooltip="'Activate User'" v-else @click="openStatusModal(rowData.id, rowData.status)"></i>

                        <i class="bi bi-person-check text-success hoverable-icon" v-tooltip="'Make Moderator'" v-if="rowData.type == 'member'" @click="openModeratorModal(rowData.id, rowData.type)"></i>
                        <i class="bi bi-person-circle text-primary hoverable-icon" v-tooltip="'Remove Moderator'" v-else @click="openModeratorModal(rowData.id, rowData.type)"></i>

                        <i class="bi bi-trash text-danger hoverable-icon" v-tooltip="'Delete Account'" @click="openDeleteModal(rowData.id)"></i>
                    </div>
                </td>
            </template>
        </EMDataTable>
    </div>

    <ConfirmationModal v-if="showDeleteModal" ref="deleteModal" :title="modal_title_text" :info_text="modal_info_text" :icon_class="modal_icon_class" :btn_text="modal_btn_text" :btn_color="modal_btn_color" @close-modal="closeDeleteModal" @perform-action="performDeleteAction"></ConfirmationModal>

    <ConfirmationModal v-if="showStatusModal" ref="statusModal" :title="modal_title_text" :info_text="modal_info_text" :icon_class="modal_icon_class" :btn_text="modal_btn_text" :btn_color="modal_btn_color" @close-modal="closeStatusModal" @perform-action="performStatusAction"></ConfirmationModal>

    <ConfirmationModal v-if="showModeratorModal" ref="moderatorModal" :title="modal_title_text" :info_text="modal_info_text" :icon_class="modal_icon_class" :btn_text="modal_btn_text" :btn_color="modal_btn_color" @close-modal="closeModeratorModal" @perform-action="performModeratorAction"></ConfirmationModal>
</template>

<script setup>
import EMDataTable from '../../components/Table/EMDataTable.vue';
import tooltip from "bootstrap/js/dist/tooltip";
import { onMounted, ref } from 'vue';
import apiClient from "@/utils/axios";
import { useHttpErrorManager } from '@/composables/useHttpErrorManager';
import ConfirmationModal from '@/components/GenericModalAndViews/ConfirmationModal.vue';
import { useAppHelpers } from '@/composables/useAppHelpers';

const helper = useAppHelpers();

const adminTabItems = ref(null);
onMounted(() => {
    helper.loaderOn(adminTabItems.value);
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
    const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new tooltip(tooltipTriggerEl))
});

const membersTable = ref(null);
const columnDef = ref([
    {
        key: 'username',
        label: 'Name',
        sortable: true,
        isSlot: true,
        width: 50
    },
    {
        key: "total_surveys",
        label: "Stats",
        class: "text-center",
        header_class: "text-center",
        width: 15
    },
    {
        key: "status",
        label: "Status",
        class: "text-center",
        header_class: "text-center",
        sortable: true,
        isSlot: true,
        width: 20
    },
    {
        key: "action",
        label: "Action(s)",
        header_class: "text-end",
        isSlot: true,
        width: 15
    }

]);

const modal_title_text = ref('');
const modal_info_text = ref('');
const modal_icon_class = ref('');
const modal_btn_text = ref('');
const modal_btn_color = ref('');

const deleteModal = ref(null);
const showDeleteModal = ref(false);
const deleteUserId = ref(null);
const openDeleteModal = (id) => {
    deleteUserId.value = id;
    modal_title_text.value = "Delete Account";
    modal_info_text.value = "Are you sure you want to delete the account of this user!";
    modal_btn_text.value = "Yes, Delete This Account!";
    modal_icon_class.value = "bi-trash text-danger";
    modal_btn_color.value = 'btn-danger';
    showDeleteModal.value = true;
}
const closeDeleteModal = () => {
    deleteUserId.value = null;
    modal_title_text.value = "";
    modal_info_text.value = "";
    modal_btn_text.value = "";
    modal_icon_class.value = "";
    modal_btn_color.value = '';
    showDeleteModal.value = false;
}
const performDeleteAction = async () => {
    if (deleteUserId.value) {
        let url = "admin/members/delete/" + deleteUserId.value;
        try {
            let response = await apiClient.post(url, {});
            if (response.status === 200) {
                membersTable.value.refresh();
                deleteModal.value.close();
            }
        }
        catch (error) {
            let errorObj = await useHttpErrorManager().handleError(error, false);
        }
    }
    else {
        deleteModal.value.close();
    }
}

const statusModal = ref(null);
const showStatusModal = ref(false);
const statusUserId = ref(null);
const currentUserStatus = ref('');
const openStatusModal = (id, status) => {
    statusUserId.value = id;
    currentUserStatus.value = status;
    modal_title_text.value = "Activate User?";
    modal_info_text.value = "Are you sure you want to activate this user's account!";
    modal_btn_text.value = "Yes, Activate it";
    modal_icon_class.value = "bi-check-circle text-info";
    modal_btn_color.value = 'btn-info';
    if (currentUserStatus.value === "active") {
        modal_title_text.value = "Block User?";
        modal_info_text.value = "Are you sure you want to block this user's account!";
        modal_btn_text.value = "Yes, Block it";
        modal_icon_class.value = "bi-ban text-warning";
        modal_btn_color.value = 'btn-warning';
    }
    showStatusModal.value = true;
}
const closeStatusModal = () => {
    statusUserId.value = null;
    currentUserStatus.value = '';
    modal_title_text.value = "";
    modal_info_text.value = "";
    modal_btn_text.value = "";
    modal_icon_class.value = "";
    modal_btn_color.value = '';
    showStatusModal.value = false;
}
const performStatusAction = async () => {
    if (statusUserId.value) {
        let url = "/admin/members/change_status/" + statusUserId.value;
        try {
            let response = await apiClient.post(url, {});
            if (response.status === 200) {
                membersTable.value.refresh();
            }
            statusModal.value.close();
        }
        catch (error) {
            let errorObj = await useHttpErrorManager().handleError(error, false);
        }
    }
    else {
        statusModal.value.close();
    }
}

const moderatorModal = ref(null);
const showModeratorModal = ref(false);
const moderatorUserId = ref(null);
const currentUserType = ref('');
const openModeratorModal = (id, type) => {
    moderatorUserId.value = id;
    currentUserType.value = type;
    modal_title_text.value = "Remove Moderator";
    modal_info_text.value = "Do you want to remove this user from being a Moderator!";
    modal_btn_text.value = "Revoke Moderator Access";
    modal_icon_class.value = "bi-person-circle text-primary";
    modal_btn_color.value = 'btn-dark';
    if (currentUserType.value === "member") {
        modal_title_text.value = "Make Moderator";
        modal_info_text.value = "Do you want to make this user a Moderator!";
        modal_btn_text.value = "Grant Moderator Access";
        modal_icon_class.value = "bi-person-check text-success";
        modal_btn_color.value = 'btn-success';
    }
    showModeratorModal.value = true;
}
const closeModeratorModal = () => {
    moderatorUserId.value = null;
    currentUserType.value = '';
    modal_title_text.value = "";
    modal_info_text.value = "";
    modal_btn_text.value = "";
    modal_icon_class.value = "";
    modal_btn_color.value = '';
    showModeratorModal.value = false;
}
const performModeratorAction = async () => {
    if (moderatorUserId.value) {
        let url = "/admin/members/manage_moderator/" + moderatorUserId.value;
        try {
            let response = await apiClient.post(url, {});
            if (response.status === 200) {
                membersTable.value.refresh();
            }
            moderatorModal.value.close();
        }
        catch (error) {
            let errorObj = await useHttpErrorManager().handleError(error, false);
        }
    }
    else {
        moderatorModal.value.close();
    }
}


</script>